package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginepersonal.ZeitApi
import org.example.project.enginepersonal.ZeitMonth
import org.example.project.enginepersonal.ZeitMobileDayInfo
import org.example.project.enginepersonal.ZeitMobileMonthSummary
import org.example.project.enginepersonal.ZeitprofilConfig
import org.example.project.enginepersonal.defaultZeitprofil
import org.example.project.enginepersonal.zeitFormatHours
import org.example.project.enginepersonal.zeitFormatSignedHours
import org.example.project.enginepersonal.zeitHourSummaryValues
import org.example.project.enginepersonal.zeitMobileDayInfo
import org.example.project.enginepersonal.zeitMobileDayRows
import org.example.project.enginepersonal.zeitMobileMonthLabel
import org.example.project.enginepersonal.zeitMobileMonthSummary
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.ZeitkontoJournalEintrag
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import kotlin.js.Date
import kotlinx.browser.window

@Composable
private fun MitarbeiterZeitkontoMetric(label: String, value: String) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.metricItem) }) {
        Span({ classes(MitarbeiterZeitkontoMobileStyles.metricLabel) }) { Text(label) }
        Span({ classes(MitarbeiterZeitkontoMobileStyles.metricValue) }) { Text(value) }
    }
}

@Composable
private fun MitarbeiterZeitkontoInfo(label: String, value: String) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.infoRow) }) {
        Span({ classes(MitarbeiterZeitkontoMobileStyles.infoLabel) }) { Text(label) }
        Span({ classes(MitarbeiterZeitkontoMobileStyles.infoValue) }) { Text(value) }
    }
}

@Composable
private fun MitarbeiterZeitkontoHinweis(text: String, error: Boolean = false) {
    Div({
        classes(
            if (error) MitarbeiterZeitkontoMobileStyles.error
            else MitarbeiterZeitkontoMobileStyles.notice
        )
    }) { Text(text) }
}

@Composable
fun MitarbeiterZeitkonto() {
    val userId = LoginService.getId()
    val refreshVersion = maxOf(
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("urlaub")
    )
    var viewportWidth by remember { mutableStateOf(window.innerWidth.toDouble()) }
    var month by remember { mutableStateOf(mitarbeiterCurrentZeitMonth()) }
    var timeDays by remember { mutableStateOf<List<All>>(emptyList()) }
    var vacations by remember { mutableStateOf<List<All>>(emptyList()) }
    var profileConfig by remember { mutableStateOf(ZeitprofilConfig()) }
    var allowance by remember { mutableStateOf<org.example.project.screens_cus.Urlaubsanspruch?>(null) }
    var selectedDate by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf("") }
    var loadedOnce by remember { mutableStateOf(false) }
    val tabletLayout = viewportWidth >= 680.0

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { viewportWidth = window.innerWidth.toDouble() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    LaunchedEffect(userId, refreshVersion, month.year) {
        if (!loadedOnce) loading = true
        error = ""
        runCatching {
            val loadedProfiles = ZeitApi.loadProfiles().let { loaded ->
                if (loaded.profile.isEmpty()) ZeitprofilConfig(profile = listOf(defaultZeitprofil())) else loaded
            }
            val loadedTimeDays = ZeitApi.loadMyYear(month.year)
            val loadedVacations = ZeitApi.loadMyBookedAbsences(month.year)
            val loadedAllowance = UrlaubsApi.loadMyAllowance(month.year)
            Triple(loadedProfiles, loadedTimeDays, loadedVacations) to loadedAllowance
        }.onSuccess { result ->
            profileConfig = result.first.first
            timeDays = result.first.second
            vacations = result.first.third
            allowance = result.second
            loadedOnce = true
        }.onFailure {
            profileConfig = ZeitprofilConfig(profile = listOf(defaultZeitprofil()))
            timeDays = emptyList()
            vacations = emptyList()
            allowance = null
            error = it.message ?: "Zeitkonto konnte nicht geladen werden."
        }
        loading = false
    }

    val summary = remember(userId, timeDays, vacations, month, profileConfig) {
        zeitMobileMonthSummary(userId, timeDays, vacations, month, profileConfig)
    }

    Style(MitarbeiterZeitkontoMobileStyles); Style(MitarbeiterZeitkontoTabletStyles)
    Div({ classes(MitarbeiterZeitkontoMobileStyles.page) }) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.header) }) {
            H3({ classes(MitarbeiterZeitkontoMobileStyles.title) }) { Text("Zeitkonto") }
            if (tabletLayout) {
                Div({ classes(MitarbeiterZeitkontoMobileStyles.rangeHeader) }) {
                    LumaticaButton("<", onClick = {
                        month = month.shift(-1)
                        selectedDate = null
                    }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeMonthLabel) }) { Text(zeitMobileMonthLabel(month)) }
                    LumaticaButton(">", onClick = {
                        month = month.shift(1)
                        selectedDate = null
                    }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                }
            } else {
                Div({ classes(MitarbeiterZeitkontoMobileStyles.rangeHeader) }) {
                    LumaticaButton("<", onClick = {
                        month = month.shift(-1)
                        selectedDate = null
                    }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeMonthLabel) }) {
                        Text(zeitMobileMonthLabel(month))
                    }
                    LumaticaButton(">", onClick = {
                        month = month.shift(1)
                        selectedDate = null
                    }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                }
            }
        }

        when {
            loading -> MitarbeiterZeitkontoHinweis("Zeitkonto wird geladen.")
            error.isNotBlank() -> MitarbeiterZeitkontoHinweis(error, error = true)
            tabletLayout -> {
                val tabletSelectedDate = selectedDate
                    ?.takeIf { it.startsWith("${month.year}-${month.month.toString().padStart(2, '0')}-") }
                    ?: mitarbeiterZeitkontoDefaultDate(month)
                MitarbeiterZeitkontoTabletMonth(
                    userId = userId,
                    month = month,
                    allowance = allowance,
                    summary = summary,
                    timeDays = timeDays,
                    vacations = vacations,
                    profileConfig = profileConfig,
                    selectedDate = tabletSelectedDate,
                    onSelectDate = { selectedDate = it }
                )
            }
            else -> {
                MitarbeiterZeitkontoMobileMonth(
                    userId = userId,
                    month = month,
                    allowance = allowance,
                    summary = summary,
                    timeDays = timeDays,
                    vacations = vacations,
                    profileConfig = profileConfig,
                    selectedDate = selectedDate?.takeIf { it.startsWith("${month.year}-${month.month.toString().padStart(2, '0')}-") },
                    onSelectDate = { selectedDate = it },
                    onCloseDay = { selectedDate = null },
                    onShiftDate = { nextDate ->
                        selectedDate = nextDate
                        month = mitarbeiterZeitMonthFromIso(nextDate)
                    }
                )
            }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoMobileMonth(
    userId: String,
    month: ZeitMonth,
    allowance: org.example.project.screens_cus.Urlaubsanspruch?,
    summary: ZeitMobileMonthSummary,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    selectedDate: String?,
    onSelectDate: (String) -> Unit,
    onCloseDay: () -> Unit,
    onShiftDate: (String) -> Unit
) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.mobileMonthStack) }) {
        if (selectedDate == null) {
            Div({ classes(MitarbeiterZeitkontoMobileStyles.metricsPanel) }) {
                Div({ classes(MitarbeiterZeitkontoMobileStyles.metricGroup) }) {
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.metricGroupTitle) }) { Text("Aktueller Stand") }
                    Div({ classes(MitarbeiterZeitkontoMobileStyles.metricGrid) }) {
                        MitarbeiterZeitkontoMetric("Stunden", allowance?.stundenkonto?.let(::mitarbeiterStundenText) ?: "-")
                        MitarbeiterZeitkontoMetric("Kontostand", allowance?.zeitkontoVerfuegbar?.let(::mitarbeiterStundenText) ?: "-")
                    }
                }
                Div({ classes(MitarbeiterZeitkontoMobileStyles.metricGroup) }) {
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.metricGroupTitle) }) { Text("Monatsleistung") }
                    Div({ classes(MitarbeiterZeitkontoMobileStyles.metricGrid) }) {
                        MitarbeiterZeitkontoMetric("Iststunden", summary.actual)
                    }
                }
            }
            MitarbeiterZeitkontoKalender(
                userId = userId,
                month = month,
                timeDays = timeDays,
                vacations = vacations,
                profileConfig = profileConfig,
                selectedDate = selectedDate,
                onSelect = onSelectDate
            )
        } else {
            MitarbeiterZeitkontoMobileDayFullscreen(
                userId = userId,
                date = selectedDate,
                timeDays = timeDays,
                vacations = vacations,
                profileConfig = profileConfig,
                onClose = onCloseDay,
                onShiftDate = { delta -> onShiftDate(mitarbeiterShiftIsoDate(selectedDate, delta)) }
            )
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoMobileDayFullscreen(
    userId: String,
    date: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    onClose: () -> Unit,
    onShiftDate: (Int) -> Unit
) {
    Div({
        classes(MitarbeiterZeitkontoMobileStyles.mobileDayFullscreen)
        onClick { onClose() }
    }) {
        Div({
            classes(MitarbeiterZeitkontoMobileStyles.mobileDayCard)
            onClick { it.stopPropagation() }
        }) {
            Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupTop) }) {
                LumaticaButton("<", onClick = { onShiftDate(-1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                H3({ classes(MitarbeiterZeitkontoMobileStyles.dialogTitle) }) {
                    Text("${mitarbeiterKalenderWochentag(date)} ${mitarbeiterZeitkontoFullDateLabel(date)}")
                }
                LumaticaButton(">", onClick = { onShiftDate(1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
            }
            MitarbeiterZeitkontoMobileDayDetails(
                userId = userId,
                date = date,
                timeDays = timeDays,
                vacations = vacations,
                profileConfig = profileConfig,
                showTitle = false
            )
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoKernDaten(
    allowance: org.example.project.screens_cus.Urlaubsanspruch?,
    summary: ZeitMobileMonthSummary
) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.mobileTimeMetrics) }) {
        MitarbeiterZeitkontoMetric("Ist", summary.actual)
        MitarbeiterZeitkontoMetric("Zeitkonto", allowance?.stundenkonto?.let(::mitarbeiterStundenText) ?: "-")
        MitarbeiterZeitkontoMetric("Reserve", allowance?.zeitreserve?.let(::mitarbeiterStundenText) ?: "-")
        MitarbeiterZeitkontoMetric("Verfügbar", allowance?.zeitkontoVerfuegbar?.let(::mitarbeiterStundenText) ?: "-")
    }
}

@Composable
private fun MitarbeiterZeitkontoKalender(
    userId: String,
    month: ZeitMonth,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    selectedDate: String?,
    onSelect: (String) -> Unit
) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.mobileTimeCalendar) }) {
        listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So").forEach { label ->
            Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeWeekday) }) { Text(label) }
        }
        mitarbeiterMonthCells("${month.year}-${month.month.toString().padStart(2, '0')}").forEach { day ->
            if (day.isBlank()) {
                Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeEmptyDay) }) {}
            } else {
                val info = zeitMobileDayInfo(userId, day, timeDays, vacations, profileConfig)
                MitarbeiterZeitkontoDayButton(day, info, selected = day == selectedDate) { onSelect(day) }
            }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoDayButton(day: String, info: ZeitMobileDayInfo, selected: Boolean = false, onClick: () -> Unit) {
    Button(attrs = {
        classes(MitarbeiterZeitkontoMobileStyles.timeDay)
        if (selected) classes(MitarbeiterZeitkontoMobileStyles.timeDaySelected)
        if (info.weekend) classes(MitarbeiterZeitkontoMobileStyles.rangeDayMuted)
        attr("title", info.label)
        style {
            info.colorHex?.let { backgroundColor(Color(it)) }
            if (info.today) {
                property("box-shadow", "inset 0 0 0 2px #ff2f46")
            }
        }
        onClick { onClick() }
    }) {
        Span({ classes(MitarbeiterZeitkontoMobileStyles.timeDayNumber) }) {
            Text(day.takeLast(2).trimStart('0').ifBlank { "0" })
        }
        if (info.hoursLabel.isNotBlank()) {
            Span({ classes(MitarbeiterZeitkontoMobileStyles.timeDayHours) }) { Text(info.hoursLabel) }
        }
        if (info.warning) {
            Span({ classes(MitarbeiterZeitkontoMobileStyles.timeDayWarning) }) { Text("!") }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoInlineDayCard(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    selectedDate: String,
    onShiftDate: (Int) -> Unit
) {
    val rows = zeitMobileDayRows(userId, selectedDate, timeDays, vacations, profileConfig)
    Div({ classes(MitarbeiterZeitkontoMobileStyles.timeInlineCard) }) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupTop) }) {
            LumaticaButton("<", onClick = { onShiftDate(-1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
            H3({ classes(MitarbeiterZeitkontoMobileStyles.dialogTitle) }) {
                Text("${mitarbeiterKalenderWochentag(selectedDate)} ${mitarbeiterKalenderDatumsLabel(selectedDate)}")
            }
            LumaticaButton(">", onClick = { onShiftDate(1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
        }
        Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupRows) }) {
            if (rows.isEmpty()) {
                MitarbeiterZeitkontoInfo("Keine Zeitbuchung", "-")
            } else {
                rows.forEach { row -> MitarbeiterZeitkontoInfo(row.first, row.second) }
            }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoMobileDayDetails(
    userId: String,
    date: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    showTitle: Boolean = true
) {
    val rows = zeitMobileDayRows(userId, date, timeDays, vacations, profileConfig)
    Div({
        classes(MitarbeiterZeitkontoMobileStyles.timeInlineCard)
        if (!showTitle) {
            style {
                marginTop(0.px)
                padding(0.px)
                property("border", "0")
                backgroundColor(Color.transparent)
            }
        }
    }) {
        if (showTitle) {
            Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupTop) }) {
                H3({ classes(MitarbeiterZeitkontoMobileStyles.dialogTitle) }) {
                    Text("${mitarbeiterKalenderWochentag(date)} ${mitarbeiterZeitkontoFullDateLabel(date)}")
                }
            }
        }
        MitarbeiterZeitkontoDesktopDayTable(rows, mobile = true)
    }
}

@Composable
private fun MitarbeiterZeitkontoTabletMonth(
    userId: String,
    month: ZeitMonth,
    allowance: org.example.project.screens_cus.Urlaubsanspruch?,
    summary: ZeitMobileMonthSummary,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    selectedDate: String,
    onSelectDate: (String) -> Unit
) {
    val urlaubstage = mitarbeiterZeitkontoMonthAbsenceDayCount(vacations, month, "urlaub")
    val krankheitstage = mitarbeiterZeitkontoMonthAbsenceDayCount(vacations, month, "krank")
    Div({ classes(MitarbeiterZeitkontoTabletStyles.monthShell) }) {
        Div({ classes(MitarbeiterZeitkontoTabletStyles.metricHeader) }) {
            Div({ classes(MitarbeiterZeitkontoTabletStyles.metricGroup) }) {
                Span({ classes(MitarbeiterZeitkontoTabletStyles.metricGroupTitle) }) { Text("Monatsleistung") }
                Div({ classes(MitarbeiterZeitkontoTabletStyles.metricGrid) }) {
                    MitarbeiterZeitkontoMetric("Iststunden", summary.actual)
                    MitarbeiterZeitkontoMetric("Urlaubstage", urlaubstage.toString())
                    MitarbeiterZeitkontoMetric("Krankheitstage", krankheitstage.toString())
                }
            }
            Div({ classes(MitarbeiterZeitkontoTabletStyles.metricGroup) }) {
                Span({ classes(MitarbeiterZeitkontoTabletStyles.metricGroupTitle) }) { Text("Aktueller Stand") }
                Div({ classes(MitarbeiterZeitkontoTabletStyles.metricGrid) }) {
                    MitarbeiterZeitkontoMetric("Überstunden verfügbar", allowance?.zeitkontoVerfuegbar?.let(::mitarbeiterStundenText) ?: "-")
                }
            }
        }

        Div({ classes(MitarbeiterZeitkontoTabletStyles.monthWorkspace) }) {
            Div({ classes(MitarbeiterZeitkontoTabletStyles.monthCalendarPanel) }) {
                Div({ classes(MitarbeiterZeitkontoTabletStyles.monthCalendarGrid) }) {
                    listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So").forEach { label ->
                        Span({ classes(MitarbeiterZeitkontoTabletStyles.monthWeekday) }) { Text(label) }
                    }
                    mitarbeiterZeitkontoTabletMonthCells(month).forEach { day ->
                        if (day.isBlank()) {
                            Span({ classes(MitarbeiterZeitkontoTabletStyles.monthEmptyDay) }) {}
                        } else {
                            val info = zeitMobileDayInfo(userId, day, timeDays, vacations, profileConfig)
                            Button(attrs = {
                                classes(MitarbeiterZeitkontoTabletStyles.monthDay)
                                if (day == selectedDate) classes(MitarbeiterZeitkontoTabletStyles.monthDaySelected)
                                if (info.weekend) classes(MitarbeiterZeitkontoTabletStyles.monthDayMuted)
                                attr("title", info.label)
                                style {
                                    info.colorHex?.let { backgroundColor(Color(it)) }
                                    if (info.today) property("box-shadow", "inset 0 0 0 2px #ff2f46")
                                }
                                onClick { onSelectDate(day) }
                            }) {
                                Span({ classes(MitarbeiterZeitkontoTabletStyles.monthDayNumber) }) {
                                    Text(day.takeLast(2).trimStart('0').ifBlank { "0" })
                                }
                                if (info.hoursLabel.isNotBlank()) {
                                    Span({ classes(MitarbeiterZeitkontoTabletStyles.monthDayHours) }) { Text(info.hoursLabel) }
                                }
                                if (info.warning) {
                                    Span({ classes(MitarbeiterZeitkontoTabletStyles.timeDayWarning) }) { Text("!") }
                                }
                            }
                        }
                    }
                }
            }
            Div({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailPanel) }) {
                MitarbeiterZeitkontoTabletDayDetails(
                    userId = userId,
                    date = selectedDate,
                    timeDays = timeDays,
                    vacations = vacations,
                    profileConfig = profileConfig
                )
            }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoTabletDayDetails(
    userId: String,
    date: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig
) {
    val rows = zeitMobileDayRows(userId, date, timeDays, vacations, profileConfig)
    Div({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailContent) }) {
        Div({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailHeader) }) {
            Span({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailTitle) }) {
                Text("${mitarbeiterKalenderWochentag(date)} ${mitarbeiterZeitkontoFullDateLabel(date)}")
            }
            Span({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailMeta) }) {
                Text(zeitMobileDayInfo(userId, date, timeDays, vacations, profileConfig).label)
            }
        }
        Div({ classes(MitarbeiterZeitkontoTabletStyles.dayDetailRows) }) {
            MitarbeiterZeitkontoDesktopDayTable(rows)
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoDesktopDayTable(rows: List<Pair<String, String>>, mobile: Boolean = false) {
    val tableClass = if (mobile) MitarbeiterZeitkontoMobileStyles.popupTable else MitarbeiterZeitkontoTabletStyles.popupTable
    val rowClass = if (mobile) MitarbeiterZeitkontoMobileStyles.popupTableRow else MitarbeiterZeitkontoTabletStyles.popupTableRow
    val cellClass = if (mobile) MitarbeiterZeitkontoMobileStyles.popupTableCell else MitarbeiterZeitkontoTabletStyles.popupTableCell
    val valueClass = if (mobile) MitarbeiterZeitkontoMobileStyles.popupTableValue else MitarbeiterZeitkontoTabletStyles.popupTableValue
    Div({ classes(tableClass) }) {
        if (rows.isEmpty()) {
            Div({ classes(rowClass) }) {
                Span({ classes(cellClass) }) { Text("Keine Zeitbuchung") }
                Span({ classes(valueClass) }) { Text("-") }
            }
        } else {
            rows.forEach { row ->
                Div({ classes(rowClass) }) {
                    Span({ classes(cellClass) }) { Text(row.first) }
                    Span({ classes(valueClass) }) { Text(row.second) }
                }
            }
        }
    }
}

private fun mitarbeiterZeitkontoDefaultDate(month: ZeitMonth): String {
    val today = kalenderTodayIsoDate()
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    if (today.startsWith(prefix)) return today
    return "${prefix}01"
}

private fun mitarbeiterZeitkontoTabletMonthCells(month: ZeitMonth): List<String> {
    val cells = mitarbeiterMonthCells("${month.year}-${month.month.toString().padStart(2, '0')}")
    return cells + List((42 - cells.size).coerceAtLeast(0)) { "" }
}

private fun mitarbeiterZeitkontoFullDateLabel(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return mitarbeiterKalenderDatumsLabel(iso)
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun mitarbeiterZeitkontoMonthAbsenceDayCount(
    vacations: List<All>,
    month: ZeitMonth,
    term: String
): Int {
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    return vacations
        .filter { mitarbeiterZeitkontoAbsenceLabel(it).lowercase().contains(term) }
        .flatMap { vacation ->
            if (vacation.tage.isNotEmpty()) {
                vacation.tage
            } else {
                val start = vacation.datumVon.ifBlank { vacation.datum }
                val end = vacation.datumBis.ifBlank { start }
                mitarbeiterKalenderWerktage(start, end)
            }
        }
        .filter { it.startsWith(prefix) }
        .distinct()
        .size
}

private fun mitarbeiterZeitkontoAbsenceLabel(vacation: All): String =
    vacation.abwesenheitsart.ifBlank {
        vacation.bezeichnung.ifBlank {
            vacation.art.ifBlank { "Ereignis" }
        }
    }

@Composable
private fun MitarbeiterZeitkontoYearGrid(
    userId: String,
    year: Int,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig
) {
    val months = (1..12).map { ZeitMonth(year, it) }
    Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearGrid) }) {
        Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearHeaderCell, MitarbeiterZeitkontoTabletStyles.timeYearIdentityHeader) }) { Text("Monat") }
        (1..31).forEach { day ->
            Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearHeaderCell) }) { Text(day.toString()) }
        }
        months.forEach { rowMonth ->
            val monthNumber = rowMonth.month
            val monthValues = zeitHourSummaryValues(userId, timeDays, vacations, rowMonth, profileConfig)
            val monthText = mitarbeiterMonthLabel("$year-${monthNumber.toString().padStart(2, '0')}").substringBefore(" ")
            Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearMonthCell) }) {
                Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearMonthContent) }) {
                    Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearMonthName) }) { Text(monthText) }
                    Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearMonthBalance) }) {
                        Text(zeitFormatHours(monthValues.first))
                    }
                }
            }
            val daysInMonth = Date(year, monthNumber, 0).getDate().toInt()
            (1..31).forEach { dayNumber ->
                if (dayNumber > daysInMonth) {
                    Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearEmptyCell) }) {}
                } else {
                    val iso = "$year-${monthNumber.toString().padStart(2, '0')}-${dayNumber.toString().padStart(2, '0')}"
                    val info = zeitMobileDayInfo(userId, iso, timeDays, vacations, profileConfig)
                    Div({
                        classes(MitarbeiterZeitkontoTabletStyles.timeYearDayCell)
                        if (info.weekend) classes(MitarbeiterZeitkontoMobileStyles.rangeDayMuted)
                        attr("title", info.label)
                        style {
                            info.colorHex?.let { backgroundColor(Color(it)) }
                            if (info.today) property("box-shadow", "inset 0 0 0 2px #ff2f46")
                        }
                    }) {
                        Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearWeekday) }) {
                            Text(mitarbeiterKalenderWochentag(iso))
                        }
                        Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearDayNumber) }) {
                            Text(dayNumber.toString())
                        }
                        if (info.warning) {
                            Span({ classes(MitarbeiterZeitkontoTabletStyles.timeDayWarning) }) { Text("!") }
                        }
                    }
                }
            }
        }
        val annualValues = months
            .map { rowMonth -> zeitHourSummaryValues(userId, timeDays, vacations, rowMonth, profileConfig) }
            .fold(0 to 0) { total, monthValues ->
                (total.first + monthValues.first) to (total.second + monthValues.second)
            }
        Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearSummaryCell) }) {
            Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearSummaryContent) }) {
                Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearSummaryLabel) }) { Text("Jahresübersicht") }
                Span({ classes(MitarbeiterZeitkontoTabletStyles.timeYearSummaryValue) }) {
                    Text(zeitFormatHours(annualValues.first))
                }
            }
        }
        Div({ classes(MitarbeiterZeitkontoTabletStyles.timeYearLegendCell) }) {
            Text("Gebuchte Zeiten")
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoDayPopup(
    date: String,
    rows: List<Pair<String, String>>,
    onClose: () -> Unit,
    onShift: (Int) -> Unit
) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.overlay) }) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopup) }) {
            Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupTop) }) {
                LumaticaButton("<", onClick = { onShift(-1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
                H3({ classes(MitarbeiterZeitkontoMobileStyles.dialogTitle) }) {
                    Text("${mitarbeiterKalenderWochentag(date)} ${mitarbeiterKalenderDatumsLabel(date)}")
                }
                LumaticaButton(">", onClick = { onShift(1) }, variant = LumaticaButtonVariant.Standard, heightPx = 30, paddingH = 10)
            }
            Div({ classes(MitarbeiterZeitkontoMobileStyles.timePopupRows) }) {
                if (rows.isEmpty()) {
                    MitarbeiterZeitkontoInfo("Keine Zeitbuchung", "-")
                } else {
                    rows.forEach { row -> MitarbeiterZeitkontoInfo(row.first, row.second) }
                }
            }
            Div({ classes(MitarbeiterZeitkontoMobileStyles.dialogActions) }) {
                LumaticaButton("Schließen", onClick = onClose, variant = LumaticaButtonVariant.Standard)
            }
        }
    }
}


@Composable
private fun MitarbeiterZeitkontoMonthGrid(
    month: String,
    entriesByDay: Map<String, List<ZeitkontoJournalEintrag>>,
    selectedDate: String,
    onSelect: (String) -> Unit
) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.timeMonthGrid) }) {
        listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So").forEach { label ->
            Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeWeekday) }) { Text(label) }
        }
        mitarbeiterMonthCells(month).forEach { day ->
            if (day.isBlank()) {
                Span({ classes(MitarbeiterZeitkontoMobileStyles.rangeEmptyDay) }) {}
            } else {
                val entries = entriesByDay[day].orEmpty()
                val hours = mitarbeiterJournalDayHours(entries)
                val date = mitarbeiterDateFromIso(day)
                val weekend = date.getDay().toInt() in setOf(0, 6)
                Button(attrs = {
                    classes(MitarbeiterZeitkontoMobileStyles.timeDay)
                    if (weekend) classes(MitarbeiterZeitkontoMobileStyles.rangeDayMuted)
                    if (day == selectedDate) classes(MitarbeiterZeitkontoMobileStyles.timeDaySelected)
                    onClick { onSelect(day) }
                }) {
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.timeDayNumber) }) {
                        Text(day.takeLast(2).trimStart('0').ifBlank { "0" })
                    }
                    Span({ classes(MitarbeiterZeitkontoMobileStyles.timeDayHours) }) {
                        Text(if (entries.isEmpty()) "-" else mitarbeiterStundenText(hours))
                    }
                }
            }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoDayDetails(date: String, entries: List<ZeitkontoJournalEintrag>) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.daySchedule) }) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.dayScheduleHeader) }) {
            Span { Text("${mitarbeiterKalenderWochentag(date)} ${mitarbeiterKalenderDatumsLabel(date)}") }
            Span { Text(mitarbeiterStundenText(mitarbeiterJournalDayHours(entries))) }
        }
        if (entries.isEmpty()) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.emptyDetail) }) { Text("Keine Buchungen für diesen Tag.") }
        } else {
            MitarbeiterZeitkontoSummary(entries)
            entries.sortedWith(compareBy<ZeitkontoJournalEintrag> { it.buchungsuhrzeit }.thenBy { it.event })
                .forEach { entry -> MitarbeiterZeitkontoEntryCard(entry) }
        }
    }
}

@Composable
private fun MitarbeiterZeitkontoSummary(entries: List<ZeitkontoJournalEintrag>) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.metricGrid, MitarbeiterZeitkontoMobileStyles.summaryGrid) }) {
        MitarbeiterZeitkontoMetric("Soll", mitarbeiterStundenText(entries.firstOrNull { it.sollstunden != 0.0 }?.sollstunden ?: 0.0))
        MitarbeiterZeitkontoMetric("Ist", mitarbeiterStundenText(mitarbeiterJournalDayHours(entries)))
        val diff = mitarbeiterJournalDayHours(entries) - (entries.firstOrNull { it.sollstunden != 0.0 }?.sollstunden ?: 0.0)
        MitarbeiterZeitkontoMetric("Differenz", mitarbeiterStundenText(diff))
    }
}

@Composable
private fun MitarbeiterZeitkontoEntryCard(entry: ZeitkontoJournalEintrag) {
    Div({ classes(MitarbeiterZeitkontoMobileStyles.listCard) }) {
        Div({ classes(MitarbeiterZeitkontoMobileStyles.cardTopRow) }) {
            Span({ classes(MitarbeiterZeitkontoMobileStyles.cardTitle) }) {
                Text(entry.buchungsart.ifBlank { entry.event.ifBlank { entry.vorgangstyp.ifBlank { "Buchung" } } })
            }
            Span({ classes(MitarbeiterZeitkontoMobileStyles.statusPill) }) {
                Text(entry.buchungsuhrzeit.ifBlank { entry.erstelltUm.ifBlank { "-" } })
            }
        }
        Div({ classes(MitarbeiterZeitkontoMobileStyles.infoGrid) }) {
            MitarbeiterZeitkontoInfo("Kommen", mitarbeiterEntryValue(entry, "kommen"))
            MitarbeiterZeitkontoInfo("Gehen", mitarbeiterEntryValue(entry, "gehen"))
            MitarbeiterZeitkontoInfo("Pause", mitarbeiterEntryValue(entry, "pause"))
            MitarbeiterZeitkontoInfo("Stunden", mitarbeiterStundenText(mitarbeiterJournalEntryHours(entry)))
        }
    }
}


